import React, { useState } from "react";
import {
  Card,
  Table,
  Row,
  Col,
  Statistic,
  DatePicker,
  Spin,
  Alert,
  Tabs,
  Select,
  Empty,
} from "antd";
import {
  DollarOutlined,
  RiseOutlined,
  BarChartOutlined,
  UserOutlined,
  LineChartOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import PageMeta from "~/components/common/meta";
import { BillingSubNav } from "~/components/admin/billing";
import {
  useGetSalesAnalytics,
  useGetSalesReports,
  useGetRevenueByService,
  useGetTopClients,
  useGetMonthlyTrends,
} from "~/services/sales/sales.queries";
import { salesApi } from "~/services/sales/sales.api";
import { clientServices } from "~/services/client/client.services";

const { RangePicker } = DatePicker;
const { Option } = Select;

export const ScreenSales = () => {
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(
    null
  );
  const [selectedClient, setSelectedClient] = useState<string | undefined>();
  const [selectedServiceType, setSelectedServiceType] = useState<
    string | undefined
  >();
  const [activeTab, setActiveTab] = useState("overview");
  const [clients, setClients] = useState<any[]>([]);
  const [isLoadingClients, setIsLoadingClients] = useState(false);

  // Prepare filters
  const filters = {
    startDate: dateRange?.[0]?.toISOString(),
    endDate: dateRange?.[1]?.toISOString(),
    clientId: selectedClient,
    serviceType: selectedServiceType,
  };

  // Fetch data using hooks
  const {
    data: analyticsData,
    isLoading: isLoadingAnalytics,
    error: analyticsError,
  } = useGetSalesAnalytics(filters);
  const { data: reportsData, isLoading: isLoadingReports } =
    useGetSalesReports(filters);
  const { data: revenueByService, isLoading: isLoadingRevenue } =
    useGetRevenueByService(filters);
  const { data: topClients, isLoading: isLoadingTopClients } =
    useGetTopClients(filters);
  const { data: monthlyTrends, isLoading: isLoadingTrends } =
    useGetMonthlyTrends(new Date().getFullYear(), selectedClient);

  // Load clients for filter
  React.useEffect(() => {
    const loadClients = async () => {
      try {
        setIsLoadingClients(true);
        const response = await clientServices.getClients({
          page: 1,
          pageSize: 100,
        });
        setClients(response.items || []);
      } catch (error) {
        console.error("Error loading clients:", error);
      } finally {
        setIsLoadingClients(false);
      }
    };

    loadClients();
  }, []);

  // Table columns for sales reports
  const salesReportColumns = [
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      render: (date: string) => dayjs(date).format("MMM DD, YYYY"),
    },
    {
      title: "Client",
      dataIndex: "client",
      key: "client",
    },
    {
      title: "Service Type",
      dataIndex: "serviceType",
      key: "serviceType",
      render: (type: string) => type.replace(/_/g, " "),
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      render: (amount: number) => salesApi.formatCurrency(amount),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status: string) => (
        <span
          className={`px-2 py-1 rounded text-xs ${
            status === "COMPLETED"
              ? "bg-green-100 text-green-800"
              : status === "IN_PROGRESS"
              ? "bg-blue-100 text-blue-800"
              : "bg-gray-100 text-gray-800"
          }`}
        >
          {status.replace(/_/g, " ")}
        </span>
      ),
    },
  ];

  // Table columns for top clients
  const topClientsColumns = [
    {
      title: "Client",
      dataIndex: "clientName",
      key: "clientName",
    },
    {
      title: "Revenue",
      dataIndex: "revenue",
      key: "revenue",
      render: (revenue: number) => salesApi.formatCurrency(revenue),
    },
  ];

  // Table columns for revenue by service
  const revenueByServiceColumns = [
    {
      title: "Service Type",
      dataIndex: "serviceType",
      key: "serviceType",
      render: (type: string) => type.replace(/_/g, " "),
    },
    {
      title: "Revenue",
      dataIndex: "revenue",
      key: "revenue",
      render: (revenue: number) => salesApi.formatCurrency(revenue),
    },
  ];

  if (analyticsError) {
    return (
      <div>
        <PageMeta
          title="Sales Reports | Art Handler"
          description="Sales analytics and reporting"
        />
        <BillingSubNav activeKey="sales" />
        <Alert
          message="Error Loading Sales Data"
          description="There was an error loading the sales data. Please try again later."
          type="error"
          showIcon
        />
      </div>
    );
  }

  return (
    <div>
      <PageMeta
        title="Sales Reports | Art Handler"
        description="Sales analytics and reporting"
      />

      <BillingSubNav activeKey="sales" />

      {/* Date Range Filter */}
      <Card className="mb-6 shadow-sm">
        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <RangePicker
              value={dateRange}
              onChange={(dates) =>
                setDateRange(dates as [dayjs.Dayjs, dayjs.Dayjs] | null)
              }
              placeholder={["Start Date", "End Date"]}
            />
            <Select
              placeholder="Filter by client"
              style={{ width: 200 }}
              allowClear
              onChange={setSelectedClient}
              loading={isLoadingClients}
            >
              {clients.map((client) => (
                <Option key={client.id} value={client.id}>
                  {client.companyName}
                </Option>
              ))}
            </Select>
            <Select
              placeholder="Filter by service type"
              style={{ width: 200 }}
              allowClear
              onChange={setSelectedServiceType}
            >
              <Option value="COLLECTION">Collection</Option>
              <Option value="DELIVERY">Delivery</Option>
              <Option value="WAREHOUSE_SERVICE">Warehouse Service</Option>
              <Option value="ON_SITE_SERVICE">On Site Service</Option>
              <Option value="FABRICATION_SERVICE">Fabrication Service</Option>
            </Select>
          </div>
          <div className="text-sm text-gray-500">
            {dateRange && (
              <>
                Showing data from {dateRange[0].format("MMM DD, YYYY")} to{" "}
                {dateRange[1].format("MMM DD, YYYY")}
              </>
            )}
          </div>
        </div>
      </Card>

      <Tabs activeKey={activeTab} onChange={setActiveTab} className="mb-6">
        <Tabs.TabPane
          tab={
            <span>
              <BarChartOutlined />
              Overview
            </span>
          }
          key="overview"
        >
          {/* Statistics Cards */}
          <Spin spinning={isLoadingAnalytics}>
            <Row gutter={[16, 16]} className="mb-6">
              <Col xs={24} sm={12} lg={6}>
                <Card>
                  <Statistic
                    title="Total Sales"
                    value={analyticsData?.totalSales || 0}
                    precision={2}
                    valueStyle={{ color: "#3f8600" }}
                    prefix={<DollarOutlined />}
                    formatter={(value) =>
                      salesApi.formatCurrency(Number(value))
                    }
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Card>
                  <Statistic
                    title="Total Bookings"
                    value={analyticsData?.totalBookings || 0}
                    valueStyle={{ color: "#1890ff" }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Card>
                  <Statistic
                    title="Average Booking Value"
                    value={analyticsData?.averageBookingValue || 0}
                    precision={2}
                    valueStyle={{ color: "#722ed1" }}
                    prefix={<DollarOutlined />}
                    formatter={(value) =>
                      salesApi.formatCurrency(Number(value))
                    }
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Card>
                  <Statistic
                    title="Growth Rate"
                    value={analyticsData?.growth || 0}
                    precision={1}
                    valueStyle={{
                      color:
                        (analyticsData?.growth || 0) >= 0
                          ? "#3f8600"
                          : "#cf1322",
                    }}
                    prefix={<RiseOutlined />}
                    suffix="%"
                  />
                </Card>
              </Col>
            </Row>
          </Spin>

          {/* Revenue by Service Chart */}
          <Card title="Revenue by Service Type" className="mb-6">
            <Spin spinning={isLoadingRevenue}>
              {revenueByService && revenueByService.length > 0 ? (
                <Table
                  dataSource={revenueByService}
                  columns={revenueByServiceColumns}
                  pagination={false}
                  rowKey="serviceType"
                />
              ) : (
                <Empty description="No revenue data available" />
              )}
            </Spin>
          </Card>

          {/* Top Clients */}
          <Card title="Top Clients by Revenue">
            <Spin spinning={isLoadingTopClients}>
              {topClients && topClients.length > 0 ? (
                <Table
                  dataSource={topClients}
                  columns={topClientsColumns}
                  pagination={false}
                  rowKey="clientName"
                />
              ) : (
                <Empty description="No client data available" />
              )}
            </Spin>
          </Card>
        </Tabs.TabPane>

        <Tabs.TabPane
          tab={
            <span>
              <LineChartOutlined />
              Monthly Trends
            </span>
          }
          key="trends"
        >
          <Card title="Monthly Sales Trends">
            <Spin spinning={isLoadingTrends}>
              {monthlyTrends && monthlyTrends.length > 0 ? (
                <div className="space-y-4">
                  {monthlyTrends.map((trend) => (
                    <div
                      key={trend.month}
                      className="flex justify-between items-center p-3 bg-gray-50 rounded"
                    >
                      <span className="font-medium">{trend.monthName}</span>
                      <span className="text-lg font-semibold">
                        {salesApi.formatCurrency(trend.revenue)}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <Empty description="No trend data available" />
              )}
            </Spin>
          </Card>
        </Tabs.TabPane>

        <Tabs.TabPane
          tab={
            <span>
              <UserOutlined />
              Detailed Reports
            </span>
          }
          key="reports"
        >
          <Card title="Sales Reports">
            <Spin spinning={isLoadingReports}>
              {reportsData && reportsData.items.length > 0 ? (
                <Table
                  dataSource={reportsData.items}
                  columns={salesReportColumns}
                  pagination={{
                    total: reportsData.meta.totalItems,
                    pageSize: reportsData.meta.itemsPerPage,
                    current: reportsData.meta.currentPage,
                    showSizeChanger: true,
                    showQuickJumper: true,
                    showTotal: (total, range) =>
                      `${range[0]}-${range[1]} of ${total} items`,
                  }}
                  rowKey="id"
                />
              ) : (
                <Empty description="No sales reports available" />
              )}
            </Spin>
          </Card>
        </Tabs.TabPane>
      </Tabs>
    </div>
  );
};

export default ScreenSales;
